"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkHasActivityOrders = exports.useCustomOrders = void 0;
/**
 * 提供手动交易账户的订单列表
 * 需支持的功能
 * 1. 下拉刷新
 * 2. 上拉加载
 * 3. 监听websocket
 */
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_utils_lib_1 = require("trade_utils_lib");
const FutureAPI_1 = require("../../apis/FutureAPI");
const interface_1 = require("../../interface");
const interface_2 = require("../../interface");
const CustomAssetsProvider_1 = require("../CustomAssetsProvider");
/**
 *
 * @param props
 * return
 * orders 订单
 * observerWSOrders 订阅wss
 *
 */
const CustomOrdersPageLength = 12;
function mergeTimeRange(filterRange, lastTime) {
    return lastTime ? Object.assign(Object.assign({}, filterRange), { endTime: `${lastTime}` }) : filterRange;
}
function updateOrderMap(map, orderList, indexOffset = 0, resetMap = true) {
    if (resetMap) {
        map.clear();
    }
    for (let index = indexOffset; index < orderList.length; index++) {
        const order = orderList[index];
        map.set(order.id, { data: order, index });
    }
}
function isFutureOrder(order) {
    const { symbol } = order;
    return symbol.includes(".PERP");
}
function getNewState(prevState, newOrder, orderMap) {
    const newState = prevState.slice(0);
    const { status, id } = newOrder;
    const prevData = orderMap.get(id);
    const lastOrder = prevState.length ? prevState[0] : undefined;
    const isNewOrder = lastOrder ? newOrder.timestamp > lastOrder.timestamp : true;
    // 判断notifyChange
    const notifyChange = (() => {
        var _a, _b, _c, _d;
        const symbolArr = newOrder.symbol.split("/");
        const base = symbolArr.length ? symbolArr[0] : "";
        const checkBaseIsFutures = base === null || base === void 0 ? void 0 : base.endsWith(".PERP");
        const isFutures = !!newOrder.position_side || checkBaseIsFutures;
        const oriQty = String(newOrder.amount);
        const qty = String(newOrder.qty);
        // 一. 订单正常完成
        if (status === interface_1.OrderStatus.CLOSE) {
            if (isFutures) {
                if (newOrder.amount === newOrder.qty) {
                    return interface_1.OrderChangedNotifyType.complete;
                }
            }
            else {
                return interface_1.OrderChangedNotifyType.complete;
            }
        }
        const isReduceOnly = isFutures &&
            ((newOrder.side === interface_1.CustomOrderSide.SELL && newOrder.position_side === interface_1.FuturePositionSide.LONG) ||
                (newOrder.side === interface_1.CustomOrderSide.BUY && newOrder.position_side === interface_1.FuturePositionSide.SHORT));
        // FIX:使用SafeDecimal进行对比
        const isQtyChanged = !new trade_utils_lib_1.SafeDecimal(oriQty).eq(new trade_utils_lib_1.SafeDecimal(qty));
        // 二. 订单发生裁剪, 并且为第一次推送
        if (isReduceOnly && isQtyChanged) {
            // 是否为最新的单
            const isFirstOrder = (lastOrder === null || lastOrder === void 0 ? void 0 : lastOrder.id) === newOrder.id;
            // 是否是提交后的第一次推送
            const isFirstTime = ((_b = (_a = lastOrder === null || lastOrder === void 0 ? void 0 : lastOrder.info) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.state) === "SUBMITTED" && ((_d = (_c = newOrder.info) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.state) === "CONFIRMED";
            if (isFirstOrder && isFirstTime) {
                // 订单已完成 taker 单
                if (status === interface_1.OrderStatus.CLOSE) {
                    return interface_1.OrderChangedNotifyType.qtyChangedComplete;
                }
                // 订单未完成 maker 单
                if (status === interface_1.OrderStatus.OPEN) {
                    return interface_1.OrderChangedNotifyType.qtyChangedNotComplete;
                }
            }
        }
        // 三. 市价单被IOC取消或部分取消
        if (status === interface_1.OrderStatus.CANCELED && newOrder.type === interface_1.CustomOrderType.MARKET_QTY) {
            if (new trade_utils_lib_1.SafeDecimal(newOrder.filled).greaterThan(0)) {
                return interface_1.OrderChangedNotifyType.marketBeenIOCComplete;
            }
            else {
                return interface_1.OrderChangedNotifyType.marketBeenIOCCancelled;
            }
        }
    })();
    // 有订单被关闭或被撤销
    if (status !== interface_1.OrderStatus.OPEN) {
        // 现有的订单列表中不存该订单则忽略掉
        if (!prevData) {
            return { newState: prevState, notifyChange };
        }
        const offset = prevData.index;
        // 移除该订单
        newState.splice(offset, 1);
        // 从map中移除该订单
        orderMap.delete(id);
        // 更新map中后续节点的index
        updateOrderMap(orderMap, newState, offset, false);
        return { newState, notifyChange };
    }
    // 有新的活跃订单
    if (prevData) {
        // 存在该订单，则更新订单的状态
        const { index, data } = prevData;
        const newOrderData = Object.assign({}, data, newOrder);
        newState.splice(index, 1, newOrderData);
        // update map
        prevData.data = newOrderData;
        return {
            newState,
            notifyChange,
        };
    }
    // 不存在该订单
    if (!prevState.length) {
        // 当前订单列表为空
        newState.push(newOrder);
        orderMap.set(id, { data: newOrder, index: 0 });
        return {
            newState,
            notifyChange,
        };
    }
    // 当前活跃订单列表非空，需要判断下时间戳
    if (isNewOrder) {
        // 插入新的订单
        newState.unshift(newOrder);
        updateOrderMap(orderMap, newState);
        return {
            newState,
            notifyChange,
        };
    }
    return { newState: prevState, notifyChange };
}
const useCustomOrders = (props, active = true) => {
    const { apiKey, symbol, observerWSOrders, orderStates, isHistory, accountType, symbolType, timeRange, onOrderStateChanged } = props;
    const [hasMore, setHasMore] = (0, react_1.useState)(true);
    const [orders, setOrders] = (0, react_1.useState)([]);
    const [error, setError] = (0, react_1.useState)();
    const [refreshing, setRefreshing] = (0, react_1.useState)(true);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const pageCursor = (0, react_1.useMemo)(() => {
        if (orders === null || orders === void 0 ? void 0 : orders.length) {
            return orders[orders.length - 1].next_page_cursor;
        }
        return undefined;
    }, [orders]);
    const initSuccess = (0, react_1.useRef)(false);
    /**校准之后的时间区间，结束时间需要加一天 */
    // FIXME:  先注释掉时间筛选
    // const timeRangeAdjusted = useMemo(() => adjustTimeRange(timeRange), [timeRange]);
    // record onOrderStateChanged callback
    const changeOrderRef = (0, react_1.useRef)(onOrderStateChanged);
    changeOrderRef.current = onOrderStateChanged;
    /**缓存当前订单的map，用于快速查询订单 */
    const orderMapRef = (0, react_1.useRef)(new Map());
    const filterRef = (0, react_1.useRef)({ symbolType, symbol });
    const orderStatesValue = (orderStates || []).join(",");
    const loadingRef = (0, react_1.useRef)(false);
    // record filter symbol type & symbol
    (0, react_1.useEffect)(() => {
        filterRef.current = { symbolType, symbol };
    }, [symbolType, symbol]);
    const queryOrder = (0, react_1.useCallback)((page_cursor) => {
        const request = { accountType: accountType, limit: CustomOrdersPageLength };
        symbol && (request.symbol = symbol);
        symbolType && (request.symbol_type = symbolType);
        // FIXME:  先去掉时间筛选相关的逻辑
        // // 设置按照时间筛选的参数
        // const range = mergeTimeRange(timeRangeAdjusted, lastTime);
        // if (range) {
        //     range.endTime && (request.end_time = range.endTime);
        //     range.startTime && (request.start_time = range.startTime);
        // }
        if (page_cursor) {
            request.page_cursor = page_cursor;
        }
        if (isHistory) {
            // 只有在历史订单中才可以按照状态筛选
            if (orderStatesValue) {
                request.state = orderStatesValue;
            }
            request.order_mode = interface_2.QueryOrderMode.NORMAL;
        }
        return isHistory ? (0, FutureAPI_1.queryHistoryOrders)(request) : (0, FutureAPI_1.queryActiveOrders)(request);
    }, [isHistory, orderStatesValue, symbol, symbolType, accountType]);
    const _refresh = (0, react_1.useCallback)(() => {
        if (!queryOrder) {
            return (0, rxjs_1.throwError)({ code: -1, message: "No queryOrder function" });
        }
        return queryOrder().pipe((0, operators_1.tap)((data) => {
            const newData = data || [];
            updateOrderMap(orderMapRef.current, newData);
            setError(null);
            setHasMore(newData.length >= CustomOrdersPageLength);
            setOrders(newData);
            initSuccess.current = true;
        }), (0, operators_1.catchError)((error) => {
            setError(error);
            setHasMore(false);
            const nullData = [];
            updateOrderMap(orderMapRef.current, nullData);
            setOrders(nullData);
            throw error;
        }));
    }, [queryOrder]);
    const refreshWithLoading = (0, react_1.useCallback)(() => {
        // FIX:在查看历史订单时，用户可能会快速切换筛选项，所以关闭防重入逻辑
        if (!isHistory && loadingRef.current) {
            return;
        }
        setRefreshing(true);
        loadingRef.current = true;
        const sub = _refresh().subscribe((data) => {
            setRefreshing(false);
            loadingRef.current = false;
        }, (error) => {
            setRefreshing(false);
            loadingRef.current = false;
        });
        return () => {
            sub.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_refresh]);
    const loadMore = (0, react_1.useCallback)(() => {
        if (!queryOrder || !pageCursor || !hasMore || !(orders === null || orders === void 0 ? void 0 : orders.length) || loadingRef.current) {
            return;
        }
        loadingRef.current = true;
        setLoading(true);
        return queryOrder(pageCursor).subscribe((data) => {
            setError(null);
            if (data) {
                setHasMore(data.length >= CustomOrdersPageLength);
                setOrders((prevData) => {
                    const newData = prevData.concat(data);
                    updateOrderMap(orderMapRef.current, newData, prevData.length, false);
                    return newData;
                });
            }
            loadingRef.current = false;
            setLoading(false);
        }, (error) => {
            setHasMore(false);
            loadingRef.current = false;
            setLoading(false);
        });
    }, [queryOrder, pageCursor, hasMore, orders]);
    // wss监听某条的变化
    (0, react_1.useEffect)(() => {
        if (isHistory || !active || !observerWSOrders) {
            return;
        }
        const sub = observerWSOrders().subscribe({
            next: (data) => {
                if (!data || !data.originOrderData) {
                    return;
                }
                const { originOrderData } = data;
                const { symbol: fSymbol, symbolType: fType } = filterRef.current;
                // 过滤掉不满足条件的推送数据
                // 在筛选当前币对的情况下，过滤掉非当前币对的数据
                if (fSymbol && fSymbol !== originOrderData.symbol) {
                    return;
                }
                const isFuture = isFutureOrder(originOrderData);
                // 有合约订单被成交, 立即刷新仓位
                if (isFuture && originOrderData.status === interface_1.OrderStatus.CLOSE) {
                    (0, CustomAssetsProvider_1.refreshAccountDetail)();
                }
                // 过滤掉类型不匹配的币对
                if ((fType === "SPOT" && isFuture) || (fType === "PERP" && !isFuture)) {
                    return;
                }
                setOrders((prevState) => {
                    var _a;
                    const payload = getNewState(prevState, originOrderData, orderMapRef.current);
                    const { newState, notifyChange } = payload;
                    if (notifyChange) {
                        (_a = changeOrderRef.current) === null || _a === void 0 ? void 0 : _a.call(changeOrderRef, originOrderData, notifyChange);
                    }
                    return newState;
                });
            },
            error: () => { },
        });
        return () => {
            sub.unsubscribe();
        };
    }, [active, isHistory, observerWSOrders]);
    // 默认调起一次, 登录状态发生改变时调起
    (0, react_1.useEffect)(() => {
        // FIX: 非active重复请求的问题
        if (!active) {
            // 第一次加载数据
            if (!initSuccess.current) {
                refreshWithLoading();
            }
            return;
        }
        refreshWithLoading();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiKey, symbolType, symbol, orderStatesValue]);
    // FIXME:  先注释掉时间筛选，后续可能会支持
    // }, [apiKey, symbolType, symbol, orderStatesValue, timeRangeAdjusted]);
    // active变为活跃时自动调起一次
    const isFirstLoad = (0, react_1.useRef)(true);
    (0, react_1.useEffect)(() => {
        if (!active) {
            return;
        }
        if (isFirstLoad.current) {
            isFirstLoad.current = false;
            return;
        }
        _refresh().subscribe({ next: () => { }, error: () => { } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);
    return { orders, refreshing, loading, error, hasMore, refresh: refreshWithLoading, loadMore };
};
exports.useCustomOrders = useCustomOrders;
/** 判断是否有活跃订单 */
function checkHasActivityOrders(accountType = trade_utils_lib_1.AccountTradeType.bot) {
    return (0, FutureAPI_1.queryActiveOrders)({
        accountType: accountType,
        limit: 1,
    }).pipe((0, operators_1.map)((result) => {
        return (result || []).length > 0;
    }));
}
exports.checkHasActivityOrders = checkHasActivityOrders;
